/**
 * Players — search, the list, and the tabbed player card (ADR-047, task B3).
 *
 * The LIST keeps a single display currency per player (the account opened at
 * registration) and labels it as such — a list column cannot hold five units at
 * once without adding rupees to tethers. The per-currency truth lives one click
 * away, on the Balances tab of the card, which answers per held currency.
 *
 * Nothing on this screen writes a balance. There is no adjustment control and
 * there should not be one until it goes through the wallet service as a proper
 * `adjustment` ledger entry with a reason attached; a balance edit box in an
 * admin panel is how an operator panel becomes the second writer of money.
 */
import { useState, type ReactNode } from 'react';
import { fetchPlayers } from '../api/client.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age } from '../lib/datetime.ts';
import { Card, EmptyState, ErrorState, Money, StatusBadge, TableSkeleton } from '../ui/primitives.tsx';
import { IconPeople, IconSearch } from '../ui/icons.tsx';
import { PlayerCard } from './PlayerCard.tsx';

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = ['text', 'text', 'text', 'num', 'num', 'num', 'num', 'num', 'num'] as const;

export function Players(): ReactNode {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  const players = useAsync(() => fetchPlayers(submitted), [submitted]);
  const rows = players.data ?? [];
  const active = rows.find((p) => p.userId === selected) ?? null;

  return (
    <div className="split">
      <div className="stack">
        <form
          className="toolbar"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(query);
          }}
        >
          <span className="search">
            <IconSearch size="sm" />
            <input
              className="input"
              value={query}
              placeholder="Username, id, email or phone"
              onChange={(event) => setQuery(event.target.value)}
            />
          </span>
          <button type="submit" className="btn btn--sm btn--primary">
            <IconSearch size="sm" />
            Search
          </button>
          {submitted ? (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                setQuery('');
                setSubmitted('');
              }}
            >
              Clear
            </button>
          ) : null}
        </form>

        <Card
          title="Players"
          sub="Money columns are in each player's display currency — the card breaks it out per currency"
          icon={<IconPeople />}
          count={rows.length}
          flush
        >
          {players.error && rows.length === 0 ? (
            <ErrorState title="The player list did not load" detail={players.error} onRetry={players.reload} />
          ) : players.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconSearch size="lg" />}
              title={submitted ? 'No player matches that search' : 'No players yet'}
              hint={
                submitted
                  ? 'Search by username, user id, email or phone. Partial matches are accepted.'
                  : 'Search by username, user id, email or phone to pull up an account.'
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Username</th>
                    <th>Status</th>
                    <th>Ccy</th>
                    <th className="right">Balance</th>
                    <th className="right">Deposited</th>
                    <th className="right">Withdrawn</th>
                    <th className="right">Net</th>
                    <th className="right">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.userId}
                      className={`row--clickable${row.userId === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(row.userId)}
                    >
                      <td className="mono dim">{row.userId}</td>
                      <td className="nowrap cell-lead">{row.username}</td>
                      <td><StatusBadge status={row.status} /></td>
                      <td className="mono dim">{row.currency}</td>
                      <td className="right mono"><Money value={row.balance} /></td>
                      <td className="right mono dim"><Money value={row.depositedTotal} /></td>
                      <td className="right mono dim"><Money value={row.withdrawnTotal} /></td>
                      <td className="right mono"><Money value={row.netPosition} colour signed /></td>
                      <td className="right mono muted" title={absolute(row.lastActiveAt)}>{age(row.lastActiveAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <PlayerCard player={active} onChanged={players.reload} />
    </div>
  );
}
